import { failLoud } from '@resource-kit/core';
import { useResourceKit } from '@resource-kit/core/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/Select';
import { DateRangePicker } from '../primitives/DatePicker';
import type { Condition, OperatorId } from '@resource-kit/model';
import type { QuickFilter } from '@resource-kit/model';
import type { FilterWhitelist } from '@resource-kit/controllers';

/**
 * QuickFilters — the toolbar quick-filter row (#1392 D4).
 *
 * Each control writes to the SAME `conditions` state the QueryBuilder uses via
 * `onChange`, so there is one filter/URL source of truth. A `select` writes an
 * `eq` condition; a `daterange` writes a `between` condition. Clearing a
 * control removes the condition (`value = null`).
 */
export interface QuickFiltersProps {
  quickFilters: readonly QuickFilter[];
  conditions: readonly Condition[];
  /** Upsert (value set) or clear (value null) the condition on `field`. */
  onChange: (field: string, op: OperatorId, value: string | [string, string] | null) => void;
}

/**
 * Render-time guard (#1392 D4): every quick-filter `field` must be in the
 * resource's `filterable` whitelist, otherwise the control would write a
 * condition the backend silently drops — a "false filtered view".
 *
 * It runs in EVERY host; `failLoud` decides what the report is. Dev and test
 * get a throw at the point of the mistake, a production build and a host that
 * injects no bundler env get a `console.error` and a list that still loads,
 * just unfiltered on that field, with the existing dropped-filter banner
 * surfacing it. Reporting rather than throwing is what keeps an env-less host
 * out of an error boundary (#2189); the mode-dependence lives in `failLoud`
 * alone, not in a second predicate here.
 */
export function assertQuickFiltersWhitelisted(
  quickFilters: readonly QuickFilter[],
  filterable: FilterWhitelist | undefined,
  slug: string,
): void {
  for (const qf of quickFilters) {
    if (!filterable || !(qf.field in filterable)) {
      failLoud(
        `[ResourcePage:${slug}] quickFilter field '${qf.field}' is not in the resource's filterable whitelist — a quick filter must write a backend-accepted condition (#1392 D4).`,
      );
    }
  }
}

// Sentinel value for the "All" (clear) select item. base-ui's Select does NOT
// emit `onValueChange` for an item whose value is the empty string (it reads ""
// as "no selection"), so the clear affordance needs a non-empty value that we
// map back to "no condition". The controlled value stays empty when no
// condition is active, so the trigger shows the placeholder — the sentinel is
// only ever the transient value of a click, never a persisted selection.
const ALL_VALUE = '__all__';

/**
 * Map a select control's chosen value to the condition it writes: the clear
 * sentinels (`''` / `__all__`) remove the condition (`null`); any other value
 * is written as an `eq` condition value. Exported so the mapping is
 * unit-testable without driving the portalled Select in jsdom.
 */
export function selectQuickFilterValue(next: string): string | null {
  return next === '' || next === ALL_VALUE ? null : next;
}

function conditionValue(conditions: readonly Condition[], field: string): Condition | undefined {
  return conditions.find((c) => c.field === field);
}

export function QuickFilters({ quickFilters, conditions, onChange }: QuickFiltersProps) {
  const { t } = useResourceKit();

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="resource-quick-filters">
      {quickFilters.map((qf) => {
        const active = conditionValue(conditions, qf.field);
        if (qf.type === 'select') {
          const value = typeof active?.value === 'string' ? active.value : '';
          return (
            <Select
              key={qf.field}
              value={value}
              onValueChange={(next) => onChange(qf.field, 'eq', selectQuickFilterValue(next))}
              // `items` feeds Select.Value the label; without it the trigger
              // shows the raw stored value until the popup has mounted once.
              items={{
                [ALL_VALUE]: t('rk.resource.quickFilter.all'),
                ...Object.fromEntries(
                  qf.options.map((opt) => [opt.value, t(opt.label, { defaultValue: opt.label })]),
                ),
              }}
            >
              <SelectTrigger
                className="w-48"
                data-testid={`quick-filter-${qf.field}`}
                aria-label={qf.field}
              >
                <SelectValue placeholder={t('rk.resource.quickFilter.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>{t('rk.resource.quickFilter.all')}</SelectItem>
                {qf.options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.label, { defaultValue: opt.label })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        // daterange
        const range = Array.isArray(active?.value) ? (active.value as [string, string]) : undefined;
        return (
          <div key={qf.field} data-testid={`quick-filter-${qf.field}`}>
            <DateRangePicker
              value={range}
              onChange={(next) => onChange(qf.field, 'between', next)}
            />
          </div>
        );
      })}
    </div>
  );
}
